/**
 * The hit-again settings as one grid (user, 20/10/2026: "please improve
 * UI/UX because the buttons, everything, labels is so horrible"): a label
 * column — icon, name, ⓘ — and one control line per row, every control the
 * same 32 px height and 13 px text as the Schedule step, so the block reads
 * straight down like a form instead of a sentence of mixed sizes.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { CHOICE_CHIP, MINIMAL_FIELD } from '../../../general/controls/field-style';

/** A number box, the editor's soft grey field at the shared height. */
export const HIT_FIELD = cn(MINIMAL_FIELD, 'text-center');
/** The native select, outlined like a choice button; its chevron is drawn by HitSelect. */
export const HIT_SELECT = cn(
  CHOICE_CHIP,
  'cursor-pointer appearance-none border-input bg-background pl-3 pr-8 font-medium text-foreground outline-none hover:border-primary/50 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20',
);
/** The words after a field ("SMS", "%", "times at most"). */
export const HIT_UNIT = 'text-[13px] text-muted-foreground';

export const HitGrid: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 items-start gap-x-5 gap-y-3 sm:grid-cols-[104px_minmax(0,1fr)]" data-testid="hit-action">
    {children}
  </div>
);

interface RowProps {
  icon: LucideIcon;
  label: string;
  hint?: string;
  testId?: string;
  /** A muted line under the controls (what the choice means). */
  note?: ReactNode;
  children: ReactNode;
}

export const HitRow: FC<RowProps> = ({ icon: Icon, label, hint, testId, note, children }) => (
  <>
    <span className="flex h-8 items-center gap-2 whitespace-nowrap text-[13px] font-medium text-foreground">
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      {label}
      {hint ? <HintDot label={hint} /> : null}
    </span>
    <div className="flex min-w-0 flex-col gap-1.5" data-testid={testId}>
      <div className="flex min-h-8 flex-wrap items-center gap-2">{children}</div>
      {note ? <p className="text-xs leading-snug text-muted-foreground">{note}</p> : null}
    </div>
  </>
);
